import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, RefreshCw, Loader2, ShieldCheck, AlertTriangle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { requireAdmin } from "@/lib/admin-guard";
import {
  listReplayableEvents,
  replayPaypalWebhookEvent,
  LIVE_REPLAY_CONFIRMATION,
  type ReplayResult,
} from "@/lib/paypal-replay.functions";

export const Route = createFileRoute("/admin_/paypal/replay")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "PayPal Webhook Replay — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: PaypalReplayPage,
});

function formatTs(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PaypalReplayPage() {
  const fetchEvents = useServerFn(listReplayableEvents);
  const replay = useServerFn(replayPaypalWebhookEvent);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<ReplayResult | null>(null);
  const [running, setRunning] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["paypal-replay-events", search],
    queryFn: () => fetchEvents({ data: { search: search.trim() || undefined } }),
  });

  async function run(eventId: string) {
    setRunning(true);
    setResult(null);
    try {
      const res = await replay({
        data: {
          eventId,
          mode: liveMode ? "live" : "dry_run",
          confirm: liveMode ? confirmText : undefined,
        },
      });
      setResult(res);
      if (res.ok) {
        toast.success(
          res.mode === "dry_run"
            ? "Dry run complete — nothing was changed"
            : "Live replay processed",
        );
      } else {
        toast.error(res.error ?? "Replay failed");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Replay failed");
    } finally {
      setRunning(false);
    }
  }

  const events = data?.events ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
      </Link>
      <h1 className="font-serif text-2xl text-foreground md:text-3xl">
        PayPal webhook replay
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Re-run a captured PayPal notification through the real handler to check
        end-to-end processing. Dry run is the default: the handler reads live
        data so it takes the real branches, but every database write is recorded
        instead of executed and no emails or invoices are sent.
      </p>

      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by event id or type"
            className="min-w-[220px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => void refetch()}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={liveMode}
            onChange={(e) => {
              setLiveMode(e.target.checked);
              setConfirmText("");
            }}
            className="mt-1"
          />
          <span>
            <span className="font-medium text-foreground">Live replay</span>
            <span className="block text-xs text-muted-foreground">
              Actually processes the event — writes to subscriptions and
              memberships and can send emails. Only for recovering a webhook
              that genuinely failed.
            </span>
          </span>
        </label>

        {liveMode && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="mb-2 flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" /> This affects real billing data
            </div>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`Type ${LIVE_REPLAY_CONFIRMATION} to enable`}
              className="w-full rounded-md border border-amber-300 bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
            />
          </div>
        )}
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No captured PayPal events yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((e) => {
              const disabled =
                running || (liveMode && confirmText !== LIVE_REPLAY_CONFIRMATION);
              return (
                <li
                  key={e.event_id}
                  className={`flex flex-wrap items-center gap-3 p-3 text-sm ${
                    selected === e.event_id ? "bg-muted/50" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-foreground">
                      {e.event_type}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {e.event_id} · received {formatTs(e.received_at)}
                      {e.error ? " · errored" : e.processed_at ? " · processed" : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setSelected(e.event_id);
                      void run(e.event_id);
                    }}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-50"
                  >
                    {running && selected === e.event_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <PlayCircle className="h-3.5 w-3.5" />
                    )}
                    {liveMode ? "Replay live" : "Dry run"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {result && (
        <div className="mt-6 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            {result.mode === "dry_run" ? (
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
            {result.mode === "dry_run" ? "Dry run" : "Live replay"} ·{" "}
            {result.eventType} · {result.durationMs}ms
          </div>

          {result.error && (
            <p className="mt-2 rounded-md bg-red-50 p-2 text-sm text-red-800">
              {result.error}
            </p>
          )}

          {result.mode === "dry_run" && (
            <>
              <p className="mt-3 text-xs text-muted-foreground">
                {result.ops.length === 0
                  ? "The handler made no changes for this event."
                  : `${result.ops.length} action${result.ops.length === 1 ? "" : "s"} would have been taken. Nothing was written.`}
              </p>
              <ul className="mt-3 space-y-2">
                {result.ops.map((op, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-border bg-background p-3 text-xs"
                  >
                    <div className="font-medium text-foreground">
                      {op.kind === "db"
                        ? `${op.operation.toUpperCase()} ${op.table ?? ""}`
                        : `side effect: ${op.operation}`}
                    </div>
                    {op.filters && op.filters.length > 0 && (
                      <div className="mt-1 text-muted-foreground">
                        where {op.filters.join(" · ")}
                      </div>
                    )}
                    {op.payload !== undefined && (
                      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2">
                        {op.payload}
                      </pre>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
